// src/routes/About.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFileAlt, FaArrowRight, FaMapMarkerAlt,
  FaEnvelope, FaLinkedin, FaGithub,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";
import { normalizeMilestones } from "../admin/milestones";
import "./About.css";

// ── Animated counter ───────────────────────────────────────────────────────
const useCountUp = (target, duration = 1200, start = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    const raw = target?.toString?.() ?? "0";
    const num = parseFloat(raw);

    if (isNaN(num)) {
      setCount(raw);
      return;
    }

    const suffix = raw.replace(/[\d.]/g, "");
    const steps = 40;
    const increment = num / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step += 1;
      current = Math.min(increment * step, num);

      setCount(
        Number.isInteger(num)
          ? Math.round(current) + suffix
          : current.toFixed(1) + suffix
      );

      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    return () => clearInterval(timer);
  }, [start, target, duration]);

  return count;
};

const StatCard = ({ value, label, delay = 0, inView }) => {
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (inView && !started) {
      const t = setTimeout(() => setStarted(true), delay);
      return () => clearTimeout(t);
    }
  }, [inView, started, delay]);

  const count = useCountUp(value, 1000, started);

  return (
    <div className="about-stat card" style={{ "--delay": `${delay}ms` }}>
      <span className="about-stat__value">{started ? count : 0}</span>
      <span className="about-stat__label">{label}</span>
    </div>
  );
};

// ── Pull-quote extractor ───────────────────────────────────────────────────
const extractPullQuote = (para = "") => {
  const sentences = para
    .replace(/([.!?])\s+/g, "$1\n")
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length >= 60 && s.length <= 200);

  if (!sentences.length) return null;
  return sentences.reduce((a, b) => (b.length > a.length ? b : a), sentences[0]);
};

const PullQuote = ({ text }) =>
  text ? <blockquote className="about-pullquote">{text}</blockquote> : null;

// ── Timeline milestones ────────────────────────────────────────────────────
// Saved milestones (Site Content → About Text → Milestones) win; otherwise
// years mentioned in the bio paragraphs; otherwise a generic default.
const resolveTimeline = (about = {}) => {
  const saved = normalizeMilestones(about.milestones);
  if (saved.length) return saved.map(({ year, text }) => ({ year, snippet: text }));
  return extractTimeline(about.paragraphs || []);
};

const extractTimeline = (paragraphs = []) => {
  const hits = [];
  const re = /\b(20\d{2}|19\d{2})\b/g;

  paragraphs.forEach((para) => {
    let m;
    while ((m = re.exec(para)) !== null) {
      const year = m[1];
      const start = Math.max(0, m.index - 30);
      const end = Math.min(para.length, m.index + 50);
      const snippet = para
        .slice(start, end)
        .replace(/^[\s\S]*?(\b\w+\b)/, "$1")
        .split(/[.,;]/)[0]
        .trim();

      hits.push({ year, snippet });
    }
  });

  const seen = new Set();
  const unique = hits.filter(({ year }) => {
    if (seen.has(year)) return false;
    seen.add(year);
    return true;
  });

  unique.sort((a, b) => Number(a.year) - Number(b.year));

  return unique.length >= 2
    ? unique
    : [
        { year: "2018", snippet: "Started BSc Computer Science" },
        { year: "2021", snippet: "Built e-voting system (STACCMU)" },
        { year: "2023", snippet: "First Class Honours, BSc Computer Science" },
        { year: "2023", snippet: "Joined ICT Authority, Nairobi" },
        { year: "2024", snippet: "MSc at ELTE Budapest" },
      ];
};

// ── Stats helpers ──────────────────────────────────────────────────────────
const parseDateSafe = (value) => {
  if (!value) return null;

  if (typeof value === "object" && typeof value.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const monthsBetween = (start, end) => {
  if (!start || !end || end < start) return 0;

  let months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  if (end.getDate() < start.getDate()) months -= 1;

  return Math.max(0, months);
};

const calculateExperienceYears = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  let totalMonths = 0;

  items.forEach((item) => {
    const start = parseDateSafe(item.startDate);
    const end = item.current ? new Date() : parseDateSafe(item.endDate) || new Date();
    totalMonths += monthsBetween(start, end);
  });

  if (totalMonths <= 0) return null;

  const years = totalMonths / 12;
  return `${Math.max(1, Math.floor(years))}+`;
};

const buildMergedStats = ({
  fallbackStats = [],
  projects = [],
  certificates = [],
  experience = [],
}) => {
  const liveProjectCount = projects.length > 0 ? String(projects.length) : null;
  const liveCertificateCount = certificates.length > 0 ? String(certificates.length) : null;
  const liveExperienceYears = calculateExperienceYears(experience);

  return fallbackStats.map((stat) => {
    const label = (stat.label || "").toLowerCase();

    if (label.includes("project")) {
      return { ...stat, value: liveProjectCount || stat.value };
    }

    if (label.includes("certificate") || label.includes("certification")) {
      return { ...stat, value: liveCertificateCount || stat.value };
    }

    if (label.includes("experience")) {
      return { ...stat, value: liveExperienceYears || stat.value };
    }

    return stat;
  });
};

// ── Main component ─────────────────────────────────────────────────────────
const About = () => {
  const {
    profile,
    about,
    skills,
    projects = [],
    certificates = [],
    experience = [],
    loading,
  } = useSite();

  const statsRef = useRef(null);
  const [statsInView, setStatsInView] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsInView(true);
      },
      { threshold: 0.3 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const paragraphs = about?.paragraphs || [];
  const timeline = resolveTimeline(about || {});

  const pq1 = extractPullQuote(paragraphs[2]);
  const pq2 = extractPullQuote(paragraphs[4]);

  const socials = [
    profile?.linkedin && {
      icon: <FaLinkedin size={16} />,
      url: profile.linkedin,
      label: "LinkedIn",
    },
    profile?.github && {
      icon: <FaGithub size={16} />,
      url: profile.github,
      label: "GitHub",
    },
    profile?.email && {
      icon: <FaEnvelope size={16} />,
      url: `mailto:${profile.email}`,
      label: profile.email,
    },
  ].filter(Boolean);

  const mergedStats = useMemo(
    () =>
      buildMergedStats({
        fallbackStats: Array.isArray(about?.stats) ? about.stats : [],
        projects,
        certificates,
        experience,
      }),
    [about?.stats, projects, certificates, experience]
  );

  return (
    <div className="about-page">
      <Navbar />

      <section className="about-hero">
        <div className="about-hero__bg" />
        <div className="container about-hero__inner">
          <div className="about-hero__photo-col">
            <div
              className={`about-hero__photo-frame${
                heroLoaded ? " about-hero__photo-frame--loaded" : ""
              }`}
            >
              {loading ? (
                <div className="skeleton about-hero__photo-skeleton" />
              ) : profile?.photo ? (
                <img
                  src={profile.photo}
                  alt={profile.name || "Raphael Tildai"}
                  className="about-hero__photo"
                  onLoad={() => setHeroLoaded(true)}
                />
              ) : (
                <div className="about-hero__photo-placeholder">
                  {(profile?.name || "RT")
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)}
                </div>
              )}

              {profile?.available && (
                <div className="about-hero__badge">
                  <span className="about-hero__badge-dot" />
                  {profile.availabilityText || "Available for work"}
                </div>
              )}

              {profile?.location && (
                <div className="about-hero__location">
                  <FaMapMarkerAlt size={11} /> {profile.location}
                </div>
              )}
            </div>

            {socials.length > 0 && (
              <div className="about-hero__socials">
                {socials.map(({ icon, url, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="about-hero__social-link"
                    title={label}
                  >
                    {icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="about-hero__text">
            <p className="about-hero__eyebrow">
              <span className="about-hero__eyebrow-line" /> About Me
            </p>

            <h1 className="about-hero__name">
              {loading ? (
                <span
                  className="skeleton"
                  style={{ display: "block", height: 48, width: "80%" }}
                />
              ) : (
                profile?.name || "Raphael Tildai"
              )}
            </h1>

            <p className="about-hero__title">
              {loading ? (
                <span
                  className="skeleton"
                  style={{ display: "block", height: 22, width: "55%" }}
                />
              ) : (
                profile?.title || "Software Engineer"
              )}
            </p>

            <p className="about-hero__bio">
              {loading ? (
                <span
                  className="skeleton"
                  style={{ display: "block", height: 18, width: "90%", marginBottom: 8 }}
                />
              ) : (
                about?.shortBio || profile?.tagline || ""
              )}
            </p>

            <div className="about-hero__actions">
              <Link to="/contact" className="btn btn-primary about-hero__btn">
                Get in Touch <FaArrowRight size={13} />
              </Link>
              <Link to="/resume" className="btn btn-outline about-hero__btn">
                <FaFileAlt size={13} /> View Resume
              </Link>
            </div>
          </div>
        </div>
      </section>

      {mergedStats.length > 0 && (
        <section className="about-stats-section" ref={statsRef}>
          <div className="container about-stats-inner">
            {mergedStats.map(({ value, label }, i) => (
              <StatCard
                key={label}
                value={value}
                label={label}
                delay={i * 120}
                inView={statsInView}
              />
            ))}
          </div>
        </section>
      )}

      <section className="about-story-section">
        <div className="container about-story-inner">
          <div className="about-story__label">
            <span className="about-story__label-line" />
            <span>My Story</span>
          </div>

          <aside className="about-timeline">
            {timeline.map(({ year, snippet }, i) => (
              <div key={`${year}-${i}`} className="about-timeline__item">
                <div className="about-timeline__year">{year}</div>
                <div className="about-timeline__milestone">{snippet}</div>
              </div>
            ))}
          </aside>

          <div className="about-story__content">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="skeleton"
                    style={{
                      height: 16,
                      marginBottom: 10,
                      width: i % 3 === 2 ? "70%" : "100%",
                    }}
                  />
                ))
              : paragraphs.map((para, i) => (
                  <React.Fragment key={i}>
                    <p className="about-para">
                      {i === 0 ? (
                        <>
                          <span className="about-para__dropcap">{para.charAt(0)}</span>
                          {para.slice(1)}
                        </>
                      ) : (
                        para
                      )}
                    </p>
                    {i === 1 && <PullQuote text={pq1} />}
                    {i === 3 && <PullQuote text={pq2} />}
                  </React.Fragment>
                ))}
          </div>
        </div>
      </section>

      {Array.isArray(skills) && skills.length > 0 && (
        <section className="about-skills-section">
          <div className="container">
            <div className="about-skills-header">
              <div className="about-story__label" style={{ marginBottom: 0 }}>
                <span className="about-story__label-line" />
                <span>What I Work With</span>
              </div>

              <Link to="/projects" className="about-skills-see-all">
                See Projects <FaArrowRight size={11} />
              </Link>
            </div>

            <div className="about-skills-alt">
              {skills.map((cat, ci) => (
                <div
                  key={ci}
                  className={`about-skills-row${
                    ci % 2 === 1 ? " about-skills-row--reverse" : ""
                  }`}
                >
                  <div className="about-skills-row__text">
                    {cat.icon && <div className="about-skills-row__icon">{cat.icon}</div>}
                    <p className="about-skills-row__cat">{cat.category}</p>
                    <div className="about-skills-row__chips">
                      {(cat.items || []).map((item) => (
                        <span key={item} className="about-skill-chip">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="about-skills-row__visual">
                    <span className="about-skills-row__visual-emoji">
                      {cat.icon || "⚙️"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="about-cta-section">
        <div className="container about-cta-inner">
          <div className="about-cta-text">
            <h2 className="about-cta-heading">Let's build something together.</h2>
            <p className="about-cta-sub">
              Open to freelance, full-time roles, and interesting collaborations.
            </p>
          </div>

          <div className="about-cta-actions">
            <Link to="/contact" className="btn btn-primary">
              Contact Me
            </Link>
            <Link to="/projects" className="btn btn-outline">
              View Projects
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;